import Vue from 'vue';
import api from '@/modules/staff-notification/api/index.ts';
import cloneDeep from 'lodash/cloneDeep';
import { orderBy, sortBy } from 'lodash';

export default {
  namespaced: true,
  state: {
    notifications: [],
  },
  actions: {
    async LOAD_STAFF_NOTIFICATIONS({ commit }) {
      const notifications = await api.getStaffNotifications();
      commit('SET_STAFF_NOTIFICATIONS', notifications);
    },
    async DELETE_STAFF_NOTIFICATION({ state, commit }, notification: StaffNotification) {
      const optimisticUpdateBackup = cloneDeep(state.notifications);
      try {
        commit('SET_STAFF_NOTIFICATIONS', state.notifications.filter(({ id }) => id !== notification.id));
        await api.deleteStaffNotification(notification.id);
      } catch (e) {
        console.error(e);
        commit('SET_STAFF_NOTIFICATIONS', optimisticUpdateBackup);
      }
    },
    async PROCESS_NEW_STAFF_NOTIFICATION({ state, commit }, notification: StaffNotification) {
      notification.justReceived = true;
      commit('SET_STAFF_NOTIFICATIONS', [notification, ...state.notifications]);
    },
    async DELETE_STAFF_NOTIFICATIONS({ state, commit }) {
      const optimisticUpdateBackup = cloneDeep(state.notifications);
      try {
        commit('SET_STAFF_NOTIFICATIONS', []);
        await api.deleteStaffNotifications();
      } catch (e) {
        console.error(e);
        commit('SET_STAFF_NOTIFICATIONS', optimisticUpdateBackup);
      }
    },
    async DISMISS_NOTIFICATION({ dispatch, commit }, notification: StaffNotification): Promise<StaffNotification> {
      const dismissedNotification = { ...notification, justReceived: false };
      commit('UPDATE_STAFF_NOTIFICATION', dismissedNotification);
      return dismissedNotification;
    },
    async DISMISS_JUST_RECEIVED_NOTIFICATIONS({ getters, dispatch }) {
      getters.justReceivedNotifications.forEach((notification: StaffNotification) => dispatch('DISMISS_NOTIFICATION', notification));
    },
    async READ_NOTIFICATION({ commit }, notification: StaffNotification): Promise<StaffNotification> {
      const optimisticUpdateBackup = cloneDeep(notification);
      try {
        commit('UPDATE_STAFF_NOTIFICATION', { ...notification, readDate: new Date().toISOString() });
        const response = await api.readNotifications([notification.id]);
        return response[0];
      } catch (e) {
        console.error(e);
        commit('UPDATE_STAFF_NOTIFICATION', optimisticUpdateBackup);
        return optimisticUpdateBackup;
      }
    },
    async UNREAD_NOTIFICATION({ commit }, notification: StaffNotification): Promise<StaffNotification> {
      const optimisticUpdateBackup = cloneDeep(notification);
      try {
        commit('UPDATE_STAFF_NOTIFICATION', { ...notification, readDate: null });
        const response = await api.unreadNotifications([notification.id]);
        return response[0];
      } catch (e) {
        console.error(e);
        commit('UPDATE_STAFF_NOTIFICATION', optimisticUpdateBackup);
        return optimisticUpdateBackup;
      }
    },
    async READ_ALL_NOTIFICATIONS({ getters }) {
      try {
        if (getters.unreadNotifications.length === 0) {
          return;
        }
        await api.readNotifications(getters.unreadNotificationsIds);
      } catch (e) {
        console.error(e);
      }
    },
    async PIN_STAFF_NOTIFICATION({ commit }, notification: StaffNotification): Promise<StaffNotification> {
      const optimisticUpdateBackup = cloneDeep(notification);
      try {
        commit('UPDATE_STAFF_NOTIFICATION', { ...notification, pinnedDate: new Date().toISOString() });
        const response = await api.pinNotification(notification.id);
        return response[0];
      } catch (e) {
        console.error(e);
        commit('UPDATE_STAFF_NOTIFICATION', optimisticUpdateBackup);
        return optimisticUpdateBackup;
      }
    },
    async UNPIN_STAFF_NOTIFICATION({ commit }, notification: StaffNotification): Promise<StaffNotification> {
      const optimisticUpdateBackup = cloneDeep(notification);
      try {
        commit('UPDATE_STAFF_NOTIFICATION', { ...notification, pinnedDate: null });
        const response = await api.unpinNotification(notification.id);
        return response[0];
      } catch (e) {
        console.error(e);
        commit('UPDATE_STAFF_NOTIFICATION', optimisticUpdateBackup);
        return optimisticUpdateBackup;
      }
    },
  },
  mutations: {
    SET_STAFF_NOTIFICATIONS: (state, notifications: StaffNotification[]) => {
      state.notifications = notifications;
    },
    UPDATE_STAFF_NOTIFICATION: (state, notification: StaffNotification) => {
      const index = state.notifications.findIndex(({ id }) => id === notification.id);
      if (index >= 0) {
        Vue.set(state.notifications, index, notification);
      }
    },
  },
  getters: {
    notifications: (state): StaffNotification[] => {
      const notifications = state.notifications
        .map((notification: StaffNotification) => ({
          ...notification,
          readDate: notification.readDate ? new Date(notification.readDate) : null,
          pinnedDate: notification.pinnedDate ? new Date(notification.pinnedDate) : null,
        }));
      const pinnedNotifications = notifications.filter((notification: StaffNotification) => !!notification.pinnedDate);
      const otherNotifications = notifications.filter((notification: StaffNotification) => !notification.pinnedDate);
      return [
        ...sortBy(pinnedNotifications, ['pinnedDate']).reverse(),
        ...otherNotifications,
      ];
    },
    justReceivedNotifications: (state): StaffNotification[] => state.notifications.filter((notification: StaffNotification) => notification.justReceived === true),
    unreadNotifications: (state): StaffNotification[] => state.notifications.filter((notification: StaffNotification) => !notification.readDate),
    unreadNotificationsIds: (state, getters): number[] => getters.unreadNotifications.map((notification: StaffNotification) => notification.id),
  },
};
